import { useForm } from '@inertiajs/react';
import { Check, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { OnboardingCountryOption, OnboardingEdition } from '@/types';
import { errorMessages } from './errors';
import {
  FormDescription,
  FormItem,
  FormLabel,
  FormMessage,
  GlobalError,
} from './form-field';
import { OnboardingShell, titleFont } from './OnboardingShell';

const SHOE_SIZES = ['36', '37', '38', '39', '40', '41', '42', '43', '44'];

const formatRUT = (value: string): string => {
  // Remove all non-alphanumeric characters
  const clean = value.replace(/[^0-9kK]/g, '');

  // Don't format if empty or just the verificador
  if (clean.length <= 1) return clean;

  // Split into rut body and verificador
  const body = clean.slice(0, -1);
  const verificador = clean.slice(-1);

  // Format the body with dots
  let formatted = '';
  const reversed = body.split('').reverse().join('');

  for (let i = 0; i < reversed.length; i++) {
    if (i > 0 && i % 3 === 0) {
      formatted = `.${formatted}`;
    }
    formatted = reversed[i] + formatted;
  }

  // Add dash and verificador
  return `${formatted}-${verificador}`;
};

const validateRUT = (rut: string): boolean => {
  if (!rut) return false;

  // Check format first
  const rutRegex = /^(\d{1,2}\.\d{3}\.\d{3}[-][0-9kK]{1})$/;
  if (!rutRegex.test(rut)) return false;

  // Remove dots and dash
  const clean = rut.replace(/\./g, '').replace(/-/g, '');

  // Split body and verifier
  const body = clean.slice(0, -1);
  const verifier = clean.slice(-1).toLowerCase();

  // Calculate verifier digit
  let sum = 0;
  let multiplier = 2;

  // Reverse iteration through the body
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i]) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }

  const remainder = sum % 11;
  const calculatedVerifier = 11 - remainder;

  // Convert to verifier string
  let expectedVerifier: string;
  if (calculatedVerifier === 11) {
    expectedVerifier = '0';
  } else if (calculatedVerifier === 10) {
    expectedVerifier = 'k';
  } else {
    expectedVerifier = calculatedVerifier.toString();
  }

  return verifier === expectedVerifier;
};

// additional-info/_components/additional-info-form.tsx. The four twins differ only in the
// country picker, the id copy, the phone placeholder and whether the shoe size is asked
// for at all — all of which arrive as props (PORTING.md G5).
export function AdditionalInfoForm({
  edition,
  countryOptions,
  defaultCountry,
}: {
  edition: OnboardingEdition;
  countryOptions: OnboardingCountryOption[];
  defaultCountry: string;
}) {
  const [rutTouched, setRutTouched] = useState(false);
  const [rutValid, setRutValid] = useState<boolean | null>(null);

  const form = useForm({
    countryType: defaultCountry,
    nationalId: '',
    shoeSize: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
  });
  const { data, setData, post, processing } = form;
  const errors = errorMessages(form.errors);

  const isChile = data.countryType === edition.chileCountryValue;
  const isHost =
    edition.hostIdLabel !== null && data.countryType === edition.hostCountry;

  // The Next form kept its submit disabled until the zod schema passed; the same rules,
  // read off the current values (U2 — no zod on the client here).
  const isValid =
    data.countryType.length > 0 &&
    data.nationalId.length > 0 &&
    (!isChile || validateRUT(data.nationalId)) &&
    (!edition.collectsShoeSize || data.shoeSize.length > 0) &&
    data.emergencyContactName.length > 0 &&
    data.emergencyContactPhone.length > 0;

  const handleRutChange = (value: string) => {
    setRutTouched(true);

    if (isChile) {
      const formatted = formatRUT(value);
      setRutValid(validateRUT(formatted));
      return formatted;
    }

    return value;
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    post(`${edition.basePath}/additional-info`, { preserveScroll: true });
  };

  return (
    <OnboardingShell ph26={edition.ph26} width="max-w-2xl">
      {/* Title */}
      <div className="text-center">
        <h1
          className={`inline-block font-bold ${titleFont(edition.ph26)} text-2xl text-primary sm:text-3xl md:text-4xl`}
        >
          Información{' '}
          <span className="bg-primary px-2 py-1 text-background">Adicional</span>
        </h1>
        <p className="mt-4 font-mono text-primary/70 text-sm">
          Completa tu perfil con la información requerida
        </p>
      </div>

      {/* Form Card */}
      <div className="border-2 border-primary bg-background/80 p-6 backdrop-blur-sm sm:p-8 md:p-10">
        <form onSubmit={onSubmit} className="space-y-6">
          {/* Info Message */}
          <div className="rounded-md border border-yellow-200 bg-yellow-50 p-4">
            <p className="font-mono text-sm text-yellow-800">
              {edition.idNotice}
            </p>
          </div>

          {/* Country Type Selector */}
          <FormItem>
            <FormLabel htmlFor="countryType" error={!!errors.countryType}>
              País
            </FormLabel>
            <Select
              value={data.countryType}
              onValueChange={(value) => setData('countryType', value)}
              disabled={processing}
            >
              <SelectTrigger id="countryType">
                <SelectValue placeholder="Selecciona tu país" />
              </SelectTrigger>
              <SelectContent>
                {countryOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <FormMessage>{errors.countryType}</FormMessage>
          </FormItem>

          {/* National ID (RUT or NIN) */}
          <FormItem>
            <FormLabel htmlFor="nationalId" error={!!errors.nationalId}>
              {isChile
                ? 'RUT'
                : isHost
                  ? edition.hostIdLabel
                  : edition.nationalIdLabel}
            </FormLabel>
            <div className="relative">
              <Input
                id="nationalId"
                placeholder={
                  isChile
                    ? '12.345.678-9'
                    : isHost
                      ? (edition.hostIdPlaceholder ?? '')
                      : edition.nationalIdPlaceholder
                }
                disabled={processing}
                value={data.nationalId}
                onChange={(e) =>
                  setData('nationalId', handleRutChange(e.target.value))
                }
                className={
                  isChile && rutTouched
                    ? rutValid
                      ? 'border-green-500 pr-10'
                      : 'border-red-500 pr-10'
                    : ''
                }
              />
              {isChile && rutTouched && (
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                  {rutValid ? (
                    <Check className="h-5 w-5 text-green-500" />
                  ) : (
                    <X className="h-5 w-5 text-red-500" />
                  )}
                </div>
              )}
            </div>
            {isChile && <FormDescription>Formato: 12.345.678-9</FormDescription>}
            <FormMessage>{errors.nationalId}</FormMessage>
          </FormItem>

          {/* Shoe Size */}
          {edition.collectsShoeSize && (
            <FormItem>
              <FormLabel htmlFor="shoeSize" error={!!errors.shoeSize}>
                {edition.ph26 ? 'Talla de Zapato 👀' : 'Talla de Zapato (CL) 👀'}
              </FormLabel>
              <Select
                value={data.shoeSize}
                onValueChange={(value) => setData('shoeSize', value)}
                disabled={processing}
              >
                <SelectTrigger id="shoeSize">
                  <SelectValue placeholder="Selecciona tu talla" />
                </SelectTrigger>
                <SelectContent>
                  {SHOE_SIZES.map((size) => (
                    <SelectItem key={size} value={size}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-muted-foreground text-sm">
                {edition.ph26 ? (
                  'Escala europea (EU).'
                ) : (
                  <>
                    Talla chilena.{' '}
                    <a
                      href="https://www.nike.cl/size-guide/calzado-hombre"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:text-primary/80"
                    >
                      Ver tabla de transformación
                    </a>
                  </>
                )}
              </p>
              <FormMessage>{errors.shoeSize}</FormMessage>
            </FormItem>
          )}

          {/* Emergency Contact Name */}
          <FormItem>
            <FormLabel
              htmlFor="emergencyContactName"
              error={!!errors.emergencyContactName}
            >
              Nombre de Contacto de Emergencia
            </FormLabel>
            <Input
              id="emergencyContactName"
              placeholder="María González"
              disabled={processing}
              value={data.emergencyContactName}
              onChange={(e) => setData('emergencyContactName', e.target.value)}
            />
            <FormMessage>{errors.emergencyContactName}</FormMessage>
          </FormItem>

          {/* Emergency Contact Phone */}
          <FormItem>
            <FormLabel
              htmlFor="emergencyContactPhone"
              error={!!errors.emergencyContactPhone}
            >
              Teléfono de Contacto de Emergencia
            </FormLabel>
            <Input
              id="emergencyContactPhone"
              placeholder={edition.phonePlaceholder}
              disabled={processing}
              value={data.emergencyContactPhone}
              onChange={(e) => setData('emergencyContactPhone', e.target.value)}
            />
            <FormMessage>{errors.emergencyContactPhone}</FormMessage>
          </FormItem>

          {/* Global Error */}
          <GlobalError>{errors.base}</GlobalError>

          {/* Submit Button */}
          <div className="flex justify-center pt-4">
            <Button
              type="submit"
              size="lg"
              disabled={processing || !isValid}
              className="w-full sm:w-auto"
            >
              {processing ? 'Guardando...' : 'Continuar'}
            </Button>
          </div>
        </form>
      </div>
    </OnboardingShell>
  );
}
